/**
 * Công thức chiến đấu & kinh tế — TẤT CẢ nằm ở đây để dễ cân bằng.
 *
 * Điểm cốt lõi: công thức TRỪ CÓ SÀN theo % máu tối đa.
 *  - DEF > ATK quái  ⇒ sát thương rơi xuống sàn ⇒ gần như không mất máu (đúng ý "wave 1 vượt trội")
 *  - sàn theo % maxHP ⇒ KHÔNG BAO GIỜ bất tử tuyệt đối ⇒ không bao giờ treo màn
 *  - boss có hệ số kỹ năng lớn ⇒ xuyên DEF ⇒ mất máu thật
 */
import type { Buff, StatKey, Stats, Unit } from './types';

export const TICK_HZ = 30;
export const DT = 1 / TICK_HZ;

/** Sàn sát thương theo % máu tối đa — CHỈ áp cho elite/boss. */
export const FLOOR_NORMAL = 0; // 0%  ⇒ đòn bị chặn hoàn toàn thì KHÔNG mất máu
export const FLOOR_ELITE = 0.01; // 1% ⇒ boss luôn xuyên được giáp, không bao giờ bị "treo màn"

/** Hệ số DEF: sát thương bị trừ đi DEF * DEF_WEIGHT. */
export const DEF_WEIGHT = 0.5;

/**
 * CUỒNG NỘ — van an toàn chống treo màn.
 * Nếu một wave kéo dài quá lâu, quái mạnh dần theo thời gian.
 * Nhờ nó ta KHÔNG cần sàn sát thương cho quái thường, nên wave đầu
 * mới có cảnh "1 chấp nhiều mà không mất máu" đúng như bạn muốn.
 *
 * MỐC CUỒNG NỘ (giây kể từ đầu wave).
 *
 * ⚠️ Phải chỉnh CÙNG LÚC với `WAVE_PACE` trong `data/waves.ts`: đây là VAN AN TOÀN cho
 * wave bị kẹt, không phải hình phạt cho wave dài. Khi wave được kéo dài (×1,71) mà để mốc
 * 65s thì wave 8–10 nào cũng vượt mốc ⇒ quái +2%/giây ⇒ snowball.
 *
 * ĐO ĐƯỢC (40 trận, màn 1.1 normal, cùng `WAVE_PACE` count 1,6 · interval 3,4 · hp 1,05):
 *   · mốc  65s ⇒ qua màn 92,5% (37/40) · 302,4s
 *   · mốc 110s ⇒ qua màn 95,0% (38/40) · 301,8s   ← chọn cái này
 * Cùng độ dài wave mà qua màn cao hơn hẳn ⇒ giữ 110.
 */
export const ENRAGE_AFTER_SEC = 110;
export const ENRAGE_ATK_PER_SEC = 0.02; // +2.5% sát thương mỗi giây sau mốc

/** Boss chỉ xuyên được giáp khi sát thương đạt ít nhất mức này so với giáp tướng. */
export const BOSS_PRESSURE_MIN = 0.75;

export function enrageMultiplier(secondsInWave: number): number {
  const over = Math.max(0, secondsInWave - ENRAGE_AFTER_SEC);
  return 1 + over * ENRAGE_ATK_PER_SEC;
}

export interface DamageInput {
  atk: number;
  multiplier?: number;
  def: number;
  targetMaxHp: number;
  isEliteOrBoss?: boolean;
  /** bỏ qua sàn (dùng cho sát thương lên Nhà) */
  ignoreFloor?: boolean;
}

export interface DamageResult {
  damage: number;
  floor: number;
  /** true nếu bị sàn chặn ⇒ đòn này "không xuyên được giáp" */
  floored: boolean;
}

/** Tính sát thương. Thuần, không phụ thuộc engine ⇒ test được bằng unit test. */
export function computeDamage(input: DamageInput): DamageResult {
  const mult = input.multiplier ?? 1;
  const raw = input.atk * mult - input.def * DEF_WEIGHT;

  // Đòn bị giáp chặn hoàn toàn
  if (raw <= 0) {
    if (input.ignoreFloor) return { damage: 1, floor: 1, floored: true };
    // Quái thường: KHÔNG mất máu ⇒ đúng cảm giác "vượt trội" ở wave đầu
    if (!input.isEliteOrBoss) return { damage: 0, floor: 0, floored: true };
    // Boss QUÁ YẾU so với giáp thì cũng bị chặn hoàn toàn:
    // boss wave 1 không làm bạn mất máu, nhưng boss về sau thì có.
    const armor = input.def * DEF_WEIGHT;
    if (input.atk < armor * BOSS_PRESSURE_MIN) {
      return { damage: 0, floor: 0, floored: true };
    }
    // Boss đủ mạnh: luôn xuyên 1% máu tối đa ⇒ không bao giờ treo màn
    const bossFloor = Math.max(1, Math.floor(FLOOR_ELITE * input.targetMaxHp));
    return { damage: bossFloor, floor: bossFloor, floored: true };
  }

  // Đòn xuyên giáp bình thường
  const floor = input.isEliteOrBoss ? Math.max(1, Math.floor(FLOOR_ELITE * input.targetMaxHp)) : 0;
  const damage = Math.max(floor, raw);
  return { damage, floor, floored: raw < floor };
}

/** Chỉ số hiệu dụng = base × buff. */
export function effectiveStat(unit: Unit, key: StatKey): number {
  let v = unit.base[key];
  for (const b of unit.buffs) {
    if (b.until <= 0) continue;
    if (b.stat === key || b.stat === 'allPower') v *= b.mult;
  }
  return v;
}

export function effectiveStats(unit: Unit): Stats {
  return {
    hp: effectiveStat(unit, 'hp'),
    atk: effectiveStat(unit, 'atk'),
    def: effectiveStat(unit, 'def'),
    mana: effectiveStat(unit, 'mana'),
    stamina: effectiveStat(unit, 'stamina'),
    atkSpeed: effectiveStat(unit, 'atkSpeed'),
    range: effectiveStat(unit, 'range'),
    moveSpeed: effectiveStat(unit, 'moveSpeed'),
    dodge: Math.min(0.75, effectiveStat(unit, 'dodge')),
    crit: Math.min(0.9, effectiveStat(unit, 'crit')),
    manaRegen: effectiveStat(unit, 'manaRegen'),
    staminaRegen: effectiveStat(unit, 'staminaRegen'),
  };
}

export function isInvulnerable(unit: Unit, now: number): boolean {
  if (now < unit.invulnUntil) return true;
  return unit.buffs.some((b) => b.stat === 'invuln' && b.until > now);
}

export function purgeExpiredBuffs(unit: Unit, now: number): void {
  if (unit.buffs.length === 0) return;
  unit.buffs = unit.buffs.filter((b) => b.until > now);
}

export function addBuff(unit: Unit, buff: Omit<Buff, 'until'> & { duration: number }, now: number): void {
  unit.buffs.push({ id: buff.id, stat: buff.stat, mult: buff.mult, until: now + buff.duration });
}

// ---------------------------------------------------------------- kinh tế

/** Giá mua tướng: tăng theo cấp số nhân ⇒ không mua hết ngay từ màn 1. */
export function defenderPrice(ownedCount: number): number {
  return Math.round(50 * Math.pow(2, Math.max(0, ownedCount - 1)));
}

/** Giá nâng cấp phòng tập luyện theo cấp. */
export function trainingCost(baseCost: number, level: number): number {
  return Math.round(baseCost * Math.pow(1.55, level));
}

/** Lợi suất giảm dần: cấp càng cao, mỗi cấp cộng càng ít (chống power creep). */
export function trainingGain(baseGain: number, level: number): number {
  const softening = 1 / (1 + level * 0.06);
  return baseGain * softening;
}

/** Vàng nhận khi giết quái. */
export function enemyGold(wave: number, isBoss: boolean, isElite: boolean): number {
  if (isBoss) return 50 + 15 * wave;
  if (isElite) return 8 + 3 * wave;
  return 3 + wave;
}

export function waveClearGold(wave: number): number {
  return 20 * wave;
}

export function stageClearGold(stageIndex: number): number {
  return Math.round(500 * (1 + 0.5 * stageIndex));
}

/** Sức mạnh nhận được từ phòng thời gian: vàng/giây ⇒ điểm sức mạnh. */
export const TIME_ROOM_GOLD_PER_SEC = 10;
export const TIME_ROOM_POWER_PER_GOLD = 0.02; // 10 vàng/giây ⇒ 0.2 điểm/giây

/** Trần chỉ số theo tiến trình màn — chống "vô địch rồi bỏ game". */
export function powerCapForStage(stageIndex: number): number {
  return 1 + 0.35 * stageIndex;
}
